import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';

export default function MinimalBetfairTest() {
    const [authCode, setAuthCode] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [popupRef, setPopupRef] = useState<Window | null>(null);
    const [accountDetails, setAccountDetails] = useState<any>(null);
    const [apiKeyState, setApiKeyState] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [manualCodeInput, setManualCodeInput] = useState<string>('');

    useEffect(() => {
        // Listen for messages from the OAuth callback window
        const handleMessage = (event: MessageEvent) => {
            console.log('=== MESSAGE RECEIVED ===');
            console.log('Origin:', event.origin);
            console.log('Data:', event.data);
            
            // Check if the message is from our callback server
            const isFromCallback = event.origin === 'https://localhost:8080' || 
                                 event.origin === 'http://localhost:8080';
                                 
            if (isFromCallback && event.data?.code) {
                console.log('Received authorization code:', event.data.code);
                setAuthCode(event.data.code);
                handleExchangeCode(event.data.code);
                
                // Close the popup if it's still open
                if (popupRef) {
                    popupRef.close();
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [popupRef]);

    const handleExchangeCode = async (code: string) => {
        try {
            setIsLoading(true);
            setError(null);
            
            const response = await api.post('/api/exchange-code', { code });
            console.log('Token exchange response:', response.data);
            
            // Get account details to verify the token works
            const accountResponse = await api.get('/api/account-details');
            setAccountDetails(accountResponse.data);
            
        } catch (err: any) {
            console.error('Error exchanging code:', err);
            setError(err.response?.data?.detail || err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTest = async () => {
        try {
            setIsLoading(true);
            setError(null);
            setAccountDetails(null);
            
            console.log('Debug: Creating API key...');
            // Create a new API key
            const createResponse = await api.post('/api/api-keys/', {
                app_key: process.env.REACT_APP_BETFAIR_APP_KEY || '',
                redirect_uri: 'https://localhost:8080/'  // Make sure this matches BETFAIR_REDIRECT_URI
            });
            
            console.log('Debug: Using redirect URI: https://localhost:8080');
            
            // Get the authorization URL from the response
            const authUrl = createResponse.data.message;
            console.log('Debug: API key created:', createResponse.data);
            
            // Extract state parameter for debugging
            const stateMatch = authUrl.match(/state=([^&]+)/);
            if (stateMatch && stateMatch[1]) {
                const state = decodeURIComponent(stateMatch[1]);
                console.log('Debug: State:', state);
                setApiKeyState(state);
            }
            
            console.log('Debug: Getting auth URL...Debug: OAuth URL:', authUrl);
            
            // Open a popup window to the authorization URL
            // Close any existing popup
            if (popupRef && !popupRef.closed) {
                try {
                    console.log('Debug: Closing existing popup');
                    popupRef.close();
                } catch (err) {
                    console.error('Debug: Error closing existing popup:', err);
                }
            }
            
            // Calculate popup dimensions
            const width = 1000;
            const height = 700;
            const left = (window.innerWidth - width) / 2;
            const top = (window.innerHeight - height) / 2;
            
            console.log('Debug: Opening popup window to', authUrl);
            const newPopup = window.open(
                authUrl,
                'betfairOAuth',
                `width=${width},height=${height},left=${left},top=${top}`
            );
            
            if (newPopup) {
                console.log('Debug: Popup opened successfully, storing reference');
                setPopupRef(newPopup);
                
                // Set up an interval to check if the popup was closed
                const checkClosed = setInterval(() => {
                    if (newPopup.closed) {
                        console.log('Debug: Popup was closed by the user');
                        clearInterval(checkClosed);
                        // If we haven't received a code after 1 second, show an error
                        setTimeout(() => {
                            if (!authCode) {
                                console.log('Debug: No auth code received after popup closed');
                                setError('The authentication window was closed, but no authorization code was received.');
                                setIsLoading(false);
                            }
                        }, 1000);
                    }
                }, 500);
            } else {
                console.error('Debug: Popup was blocked');
                setError('Popup was blocked. Please allow popups and try again.');
                setIsLoading(false);
            }
        } catch (error: any) {
            console.error('Debug: Error in handleTest:', error);
            console.error('Debug: Error response:', error.response?.data);
            setError(error.response?.data?.detail || 'Failed to initiate OAuth flow');
            setIsLoading(false);
        }
    };

    return (
        <div className="p-4">
            <h1 className="text-2xl font-bold mb-4">Betfair OAuth Test</h1>
            
            <button
                onClick={handleTest}
                disabled={isLoading}
                className={`${
                    isLoading 
                        ? 'bg-gray-500 cursor-not-allowed' 
                        : 'bg-blue-500 hover:bg-blue-700'
                } text-white font-bold py-2 px-4 rounded mb-4`}
            >
                {isLoading ? 'Processing...' : 'Test OAuth Flow'}
            </button>

            {error && (
                <div className="text-red-500 mb-4">
                    Error: {error}
                </div>
            )}

            {authCode && (
                <div className="text-green-500 mb-4">
                    Authorization Code: {authCode}
                </div>
            )}

            {!authCode && !isLoading && (
                <div className="mt-4 mb-4">
                    <p className="text-gray-700 mb-2">Enter authorization code manually if needed:</p>
                    <div className="flex">
                        <input 
                            type="text" 
                            className="border rounded py-2 px-3 mr-2 flex-grow"
                            placeholder="Paste authorization code here" 
                            value={manualCodeInput}
                            onChange={(e) => {
                                setManualCodeInput(e.target.value.trim());
                                if (e.target.value.trim().length > 10) {
                                    setAuthCode(e.target.value.trim());
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && manualCodeInput.length > 10) {
                                    setAuthCode(manualCodeInput);
                                    handleExchangeCode(manualCodeInput);
                                }
                            }}
                        />
                        <button
                            onClick={() => {
                                if (manualCodeInput.length > 10) {
                                    setAuthCode(manualCodeInput);
                                    handleExchangeCode(manualCodeInput);
                                }
                            }}
                            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                            disabled={manualCodeInput.length <= 10}
                        >
                            Submit
                        </button>
                    </div>
                </div>
            )}

            {sessionId && (
                <div className="text-blue-500 mb-4">
                    Session ID: {sessionId}
                </div>
            )}

            {accountDetails && (
                <div className="mt-4">
                    <h2 className="text-xl font-bold mb-2">Account Details</h2>
                    <pre className="bg-gray-100 p-4 rounded">
                        {JSON.stringify(accountDetails, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
}